import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  IconActivity,
  IconArrowUpRight,
  IconCheck,
  IconDeviceDesktop,
  IconFlask,
  IconPlayerPlay,
  IconPlus,
  IconRobot,
  IconSearch,
  IconWorld,
} from '@tabler/icons-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '../components/ui';
import type { ProjectType } from '@verifyloop/core';
import { describeError } from '../api/client';
import { createProject, deleteProject, listProjects, startRun } from '../api/endpoints';
import { useResource, useToast } from '../state';
import { formatRelative, pluralize } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { ErrorNotice, EmptyState, LoadingRows } from '../components/Feedback';
import { PassRateTrend } from '../components/PassRateTrend';
import { ConfirmButton, Modal, SelectField, TextField } from '../components/Form';
import { RunStatusPill } from '../components/Pills';

export function ProjectsPage(): JSX.Element {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [runningProject, setRunningProject] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'frontend' | 'backend'>('all');

  const projects = useResource(() => listProjects(), [], {
    live: ['project.updated', 'run.created', 'run.finished'],
  });

  const allProjects = projects.data?.projects ?? [];

  // Workspace executive metrics
  const stats = useMemo(() => {
    const totalProjects = allProjects.length;
    const totalTests = allProjects.reduce((acc, p) => acc + (p.testCount ?? 0), 0);
    const totalEnvs = allProjects.reduce((acc, p) => acc + (p.environmentCount ?? 0), 0);

    let passSum = 0;
    let passCount = 0;
    for (const p of allProjects) {
      if (p.latestRuns && p.latestRuns.length > 0) {
        const passed = p.latestRuns.filter((r) => r.status === 'passed').length;
        passSum += (passed / p.latestRuns.length);
        passCount++;
      }
    }
    const avgPassRate = passCount > 0 ? Math.round((passSum / passCount) * 100) : 100;
    return { totalProjects, totalTests, totalEnvs, avgPassRate };
  }, [allProjects]);

  // Filtered projects
  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allProjects.filter((p) => {
      if (typeFilter !== 'all' && p.type !== typeFilter) return false;
      if (q) {
        const matchName = p.name.toLowerCase().includes(q);
        const matchUrl = (p.url ?? '').toLowerCase().includes(q);
        return matchName || matchUrl;
      }
      return true;
    });
  }, [allProjects, typeFilter, searchQuery]);

  const runAll = async (projectId: string, name: string): Promise<void> => {
    setRunningProject(projectId);
    try {
      const accepted = await startRun({ projectId, trigger: 'api' });
      if (accepted.skipped.length > 0) {
        toast.info(
          `${pluralize(accepted.runIds.length, 'run')} started for ${name}`,
          `skipped: ${accepted.skipped.map((entry) => `${entry.testId} (${entry.reason})`).join(', ')}`,
        );
      } else {
        toast.success(
          `${pluralize(accepted.runIds.length, 'run')} started for ${name}`,
          `${accepted.creditsCharged} credit(s) reserved · results stream in live`,
        );
      }
      projects.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setRunningProject(null);
    }
  };

  return (
    <div className="page projects-page">
      <PageHeader
        title="Workspace Overview"
        subtitle="Autonomous end-to-end verification dashboard: live product testing, visual Playbook workflows, and failure forensics."
        actions={
          <Button type="button" className="btn--primary" onClick={() => setCreating(true)}>
            <IconPlus size={17} stroke={1.8} aria-hidden="true" />
            New project
          </Button>
        }
      />

      {/* 1. Modern Workspace Executive KPI Strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          className="panel"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '16px 18px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Active Projects
            </span>
            <IconWorld size={18} stroke={1.7} style={{ color: '#3b82f6' }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: 'var(--text)' }}>
            {stats.totalProjects}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {stats.totalEnvs} deployment environments
          </div>
        </div>

        <div
          className="panel"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '16px 18px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Verified Scenarios
            </span>
            <IconFlask size={18} stroke={1.7} style={{ color: '#a855f7' }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: 'var(--text)' }}>
            {stats.totalTests}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Durable automated testcases
          </div>
        </div>

        <div
          className="panel"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '16px 18px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Workspace Pass Rate
            </span>
            <IconCheck size={18} stroke={1.7} style={{ color: 'var(--pass)' }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: 'var(--pass)' }}>
            {stats.avgPassRate}%
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Calculated across recent runs
          </div>
        </div>

        <div
          className="panel"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '16px 18px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Execution &amp; Engine
            </span>
            <IconRobot size={18} stroke={1.7} style={{ color: '#0ea5e9' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--pass)', display: 'inline-block' }} />
            <strong style={{ fontSize: 14, color: 'var(--text)' }}>Playwright Ready</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            16 MCP tools · Vault active
          </div>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '10px 14px',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          marginBottom: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            className={`btn btn--small ${typeFilter === 'all' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTypeFilter('all')}
          >
            All Projects ({allProjects.length})
          </button>
          <button
            type="button"
            className={`btn btn--small ${typeFilter === 'frontend' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTypeFilter('frontend')}
          >
            <IconDeviceDesktop size={14} style={{ marginRight: 4 }} />
            Browser Apps ({allProjects.filter((p) => p.type === 'frontend').length})
          </button>
          <button
            type="button"
            className={`btn btn--small ${typeFilter === 'backend' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTypeFilter('backend')}
          >
            <IconWorld size={14} style={{ marginRight: 4 }} />
            API Services ({allProjects.filter((p) => p.type === 'backend').length})
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', minWidth: 220, position: 'relative' }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)' }} />
          <input
            type="search"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              fontSize: 12.5,
              padding: '6px 10px 6px 30px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg-inset)',
              color: 'var(--text)',
            }}
          />
        </div>
      </div>

      {projects.error ? (
        <ErrorNotice error={projects.error} onRetry={projects.reload} title="Could not load projects" />
      ) : null}

      {projects.loading ? <LoadingRows rows={3} label="Loading projects" /> : null}

      {!projects.loading && allProjects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          action={
            <Button type="button" onClick={() => setCreating(true)}>
              <IconPlus size={17} stroke={1.8} aria-hidden="true" />
              Create a project
            </Button>
          }
        >
          <p>
            A project points at one app: a deployed URL, or a port on a developer&apos;s machine reached
            through a tunnel. Then you write plans against it and the browser does the proving.
          </p>
        </EmptyState>
      ) : null}

      {filteredProjects.length > 0 ? (
        <ul className="project-grid">
          {filteredProjects.map((project) => (
            <li key={project.id}>
              <Card className="project-card">
                <CardHeader className="project-card__header">
                  <div className="project-card__identity">
                    <div className="project-card__title-row">
                      <CardTitle>
                        <Link className="project-card__title" to={`/projects/${project.id}`}>
                          {project.name}
                          <IconArrowUpRight size={16} stroke={1.7} aria-hidden="true" />
                        </Link>
                      </CardTitle>
                      <Badge variant={project.type === 'frontend' ? 'secondary' : 'outline'}>
                        {project.type === 'frontend' ? 'Browser' : 'HTTP'}
                      </Badge>
                    </div>
                    <p className="project-card__url mono" title={project.url}>
                      {project.originMode === 'local'
                        ? `localhost:${project.localPort ?? '?'}`
                        : (project.url ?? 'No URL configured')}
                    </p>
                  </div>
                  <PassRateTrend runs={project.latestRuns} />
                </CardHeader>
                <CardContent className="project-card__content">
                  <dl className="project-card__stats">
                    <div><dt>Tests</dt><dd>{project.testCount}</dd></div>
                    <div><dt>Environments</dt><dd>{project.environmentCount}</dd></div>
                    <div><dt>Updated</dt><dd>{formatRelative(project.updatedAt)}</dd></div>
                  </dl>
                  <div className="project-card__runs">
                    <div className="project-card__section-label">
                      <IconActivity size={14} stroke={1.7} aria-hidden="true" />
                      Recent runs
                    </div>
                    {project.latestRuns.length > 0 ? (
                      <ul>
                        {project.latestRuns.slice(0, 3).map((run) => (
                          <li key={run.id}>
                            <RunStatusPill status={run.status} />
                            {run.failureKind ? <span className="muted">{run.failureKind}</span> : null}
                            <span className="muted">{formatRelative(run.startedAt)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="project-card__empty">No runs recorded yet.</p>
                    )}
                  </div>
                </CardContent>
                <footer className="project-card__actions">
                  <Button asChild size="sm" variant="default" className="btn--primary">
                    <Link to={`/projects/${project.id}`}>
                      Open Playbook <IconArrowUpRight size={15} stroke={1.7} aria-hidden="true" />
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={runningProject === project.id || project.testCount === 0}
                    onClick={() => void runAll(project.id, project.name)}
                  >
                    <IconPlayerPlay size={15} stroke={1.8} aria-hidden="true" />
                    {runningProject === project.id ? 'Starting…' : 'Run tests'}
                  </Button>
                  <ConfirmButton
                    label="Delete"
                    confirmLabel="Really delete?"
                    className="btn btn--small btn--ghost"
                    onConfirm={() => {
                      void deleteProject(project.id)
                        .then(() => {
                          toast.success(`Deleted project “${project.name}”`);
                          projects.reload();
                        })
                        .catch((error: unknown) => toast.error(describeError(error)));
                    }}
                  />
                </footer>
              </Card>
            </li>
          ))}
        </ul>
      ) : null}

      {creating ? (
        <CreateProjectDialog
          onClose={() => setCreating(false)}
          onCreated={() => projects.reload()}
        />
      ) : null}
    </div>
  );
}

function CreateProjectDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<ProjectType>('frontend');
  const [origin, setOrigin] = useState<'url' | 'local'>('url');
  const [url, setUrl] = useState('');
  const [port, setPort] = useState('3000');
  const [testIdAttributes, setTestIdAttributes] = useState('data-testid');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const attributes = testIdAttributes
        .split(/[,\s]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      await createProject({
        name,
        type,
        ...(origin === 'url' ? { url } : { local: Number(port) || 3000, localHost: '127.0.0.1' }),
        ...(attributes.length > 0 ? { testIdAttributes: attributes } : {}),
      });
      toast.success(`Project “${name}” created`);
      onCreated();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-project" disabled={pending}>
            {pending ? 'Creating…' : 'Create project'}
          </Button>
        </>
      }
    >
      <form id="create-project" onSubmit={(event) => void submit(event)}>
        <TextField
          label="Name"
          value={name}
          onChange={setName}
          placeholder="Storefront"
          required
        />
        <SelectField<ProjectType>
          label="Type"
          value={type}
          onChange={setType}
          options={[
            { value: 'frontend', label: 'frontend: driven in a real browser' },
            { value: 'backend', label: 'backend: driven over HTTP' },
          ]}
        />
        <SelectField<'url' | 'local'>
          label="Where does it run"
          value={origin}
          onChange={setOrigin}
          options={[
            { value: 'url', label: 'a deployed URL' },
            { value: 'local', label: 'this machine (reached through a tunnel)' },
          ]}
        />
        {origin === 'url' ? (
          <TextField
            label="URL"
            value={url}
            onChange={setUrl}
            placeholder="https://staging.example.com"
            type="url"
            required
          />
        ) : (
          <TextField
            label="Local port"
            value={port}
            onChange={setPort}
            type="number"
            hint="Justloop reaches it over a tunnel started by `justloop tunnel start --port 3000`."
          />
        )}
        <TextField
          label="Test-id attributes"
          value={testIdAttributes}
          onChange={setTestIdAttributes}
          hint="Comma separated, in priority order. These are the attributes Justloop trusts as stable locators."
        />
        {error ? <ErrorNotice error={error} title="Could not create the project" /> : null}
      </form>
    </Modal>
  );
}
